import type {
  ClientOfferContractorData,
  ClientOfferDTO,
  ClientOfferLineItem,
  ClientOfferSurfaceSummary,
} from './clientOffer.types';
import type { OfferDocumentVariant } from './offerDocumentVariant';
import {
  calculatePercentageMoney,
  roundMoney,
  sumMoney,
} from './money';

export function sanitizeClientOfferDTO(input: ClientOfferDTO): ClientOfferDTO {
  const workNet = safeMoney(input.workNet);
  const materialNet = safeMoney(input.materialNet);
  const netTotal = sumMoney([workNet, materialNet]);
  const vatPercent = safeNumber(input.vatPercent);
  const vatAmount =
    input.vatLabel === 'VAT: do potwierdzenia'
      ? 0
      : calculatePercentageMoney(netTotal, vatPercent / 100);
  const documentVariant: OfferDocumentVariant =
    input.documentVariant === 'test' ? 'test' : 'client';

  return {
    documentVariant,
    clientName: safeString(input.clientName),
    projectName: safeString(input.projectName),
    scopeLabel: safeString(input.scopeLabel),
    sourceLabel: optionalString(input.sourceLabel),
    // A test document carries no contractor identity at all. Dropping it here
    // rather than at render time means no incomplete, wrong or sample company
    // data can reach the page by any later code path.
    contractor: documentVariant === 'test' ? undefined : copyContractor(input.contractor),
    regionLabel: optionalString(input.regionLabel),
    date: copyDate(input.date),
    estimatedAreaM2: optionalNumber(input.estimatedAreaM2),
    floorAreaM2: optionalNumber(input.floorAreaM2),
    surfaceSummary: copySurfaceSummary(input.surfaceSummary),
    usesApartmentApproximation: Boolean(input.usesApartmentApproximation),
    workNet,
    materialNet,
    netTotal,
    vatPercent,
    vatLabel: optionalString(input.vatLabel),
    vatAmount,
    grossTotal: sumMoney([netTotal, vatAmount]),
    settlementLabel: safeString(input.settlementLabel),
    materialMode: input.materialMode,
    materialLines: (input.materialLines ?? []).map((line) => ({
      label: safeString(line.label),
      quantityLabel: safeString(line.quantityLabel),
      clientPrice: safeMoney(line.clientPrice),
    })),
    items: (input.items ?? []).map(copyLineItem),
    // Lista informacyjna: czyszczona i deduplikowana, nigdy nie dotyka kwot.
    includedScopeLabels: copyIncludedScopeLabels(input.includedScopeLabels),
    excludedScopeLabels: copyIncludedScopeLabels(input.excludedScopeLabels),
    unresolvedScopeLabels: copyIncludedScopeLabels(input.unresolvedScopeLabels),
  };
}

function copyIncludedScopeLabels(labels: string[] | undefined): string[] | undefined {
  if (!labels || labels.length === 0) return undefined;

  const cleaned = [...new Set(labels.map((label) => safeString(label)).filter((label) => label.length > 0))];
  return cleaned.length > 0 ? cleaned : undefined;
}

function copyContractor(
  contractor: ClientOfferContractorData | undefined
): ClientOfferContractorData | undefined {
  if (!contractor) return undefined;

  return {
    companyName: optionalString(contractor.companyName),
    nip: optionalString(contractor.nip),
    phone: optionalString(contractor.phone),
    email: optionalString(contractor.email),
    logoDataUrl: optionalString(contractor.logoDataUrl),
    isPro: Boolean(contractor.isPro),
  };
}

function copySurfaceSummary(
  surface: ClientOfferSurfaceSummary | undefined
): ClientOfferSurfaceSummary | undefined {
  if (!surface) return undefined;

  return {
    surfaceMode: surface.surfaceMode,
    paving: Boolean(surface.paving),
    pavingWorkingAreaM2: optionalNumber(surface.pavingWorkingAreaM2),
    curbLengthM: optionalNumber(surface.curbLengthM),
    pavingThicknessLabel: optionalString(surface.pavingThicknessLabel),
    pavingSubbaseLabel: optionalString(surface.pavingSubbaseLabel),
    pavingTransportLabel: optionalString(surface.pavingTransportLabel),
    apartmentAreaM2: optionalNumber(surface.apartmentAreaM2),
    bathroomAreaM2: optionalNumber(surface.bathroomAreaM2),
    bathroomModeLabel: optionalString(surface.bathroomModeLabel),
    confirmationStatus: optionalString(surface.confirmationStatus),
    floorAreaM2: optionalNumber(surface.floorAreaM2),
    perimeterM: optionalNumber(surface.perimeterM),
    wallGrossAreaM2: optionalNumber(surface.wallGrossAreaM2),
    doorDeductionM2: optionalNumber(surface.doorDeductionM2),
    largeOpeningsDeductionM2: optionalNumber(surface.largeOpeningsDeductionM2),
    wallNetAreaM2: optionalNumber(surface.wallNetAreaM2),
    ceilingAreaM2: optionalNumber(surface.ceilingAreaM2),
    finalWorkingAreaM2: optionalNumber(surface.finalWorkingAreaM2),
  };
}

function copyLineItem(item: ClientOfferLineItem): ClientOfferLineItem {
  return {
    id: safeString(item.id),
    name: safeString(item.name),
    quantity: safeNumber(item.quantity),
    unit: safeString(item.unit),
    unitPrice: safeMoney(item.unitPrice),
    total: safeMoney(item.total),
  };
}

function safeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function optionalString(value: unknown): string | undefined {
  const normalized = safeString(value);
  return normalized || undefined;
}

function safeNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0;
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, value)
    : undefined;
}

function safeMoney(value: unknown): number {
  return roundMoney(safeNumber(value));
}

function copyDate(value: Date): Date {
  const timestamp = value instanceof Date ? value.getTime() : Number.NaN;
  return Number.isFinite(timestamp) ? new Date(timestamp) : new Date(0);
}
